import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ResponsiveTable, { ResponsiveTableColumn } from '../components/common/ResponsiveTable';
import SearchPicker, { PickerOption } from '../components/pettyCash/SearchPicker';
import HolderDialog, { HolderInput } from '../components/pettyCash/HolderDialog';
import CategoryDialog, { CategoryInput } from '../components/pettyCash/CategoryDialog';
import ProcessSettings from '../components/pettyCash/ProcessSettings';
import { MAX_FINANCE_STAGES } from '../components/pettyCash/FinanceStagesEditor';
import HandoverDialog from '../components/pettyCash/HandoverDialog';
import HandoversTab from '../components/pettyCash/HandoversTab';
import ConfirmationsTab from '../components/pettyCash/ConfirmationsTab';
import pettyCashApi from '../services/pettyCashApi';
import { showSnackbar } from '../store/slices/uiSlice';
import type { HandoverInput, PettyCashCategory, PettyCashHolder, PettyCashSettings, PettyCashSetup as SetupData } from '../types/pettyCash';
import { categoryListMatches, formatAmount, formatDate, parseAmount, processSettings, quarterLabel, signedAmount } from '../utils/pettyCash';

/**
 * Petty Cash › Setup (per company): the settings (VAT codes, cash supplier, limits, approval, the warehouse
 * whose G/L rules department claims follow; the imprest check, reimbursement, quarterly confirmation, original
 * receipts and finance stages), the petty cash holders and their SAP accounts (and their handovers), the expense
 * categories (an SAP item or class per allocation type, whose Advanced G/L rules give the account), and the
 * holders' quarterly confirmations.
 */

const REQUISITION_TYPES = ['Service', 'Inventory', 'FixedAsset'];
const errorText = (e: any, fallback: string): string => e?.response?.data?.message || e?.message || fallback;

type SetupTab = 'settings' | 'holders' | 'handovers' | 'confirmations' | 'categories';

const PettyCashSetup: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [companyId, setCompanyId] = useState<number>(() => Number(localStorage.getItem('selectedCompanyId') || '') || 0);
  const [setup, setSetup] = useState<SetupData | null>(null);
  const [settings, setSettings] = useState<PettyCashSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<SetupTab>('settings');
  const [saving, setSaving] = useState(false);
  const [holderDialog, setHolderDialog] = useState<{ open: boolean; holder: PettyCashHolder | null }>({ open: false, holder: null });
  const [categoryDialog, setCategoryDialog] = useState<{ open: boolean; category: PettyCashCategory | null }>({ open: false, category: null });
  const [handoverDialog, setHandoverDialog] = useState<{ open: boolean; holder: PettyCashHolder | null }>({ open: false, holder: null });
  const [handoversKey, setHandoversKey] = useState(0);
  const [starterBusy, setStarterBusy] = useState(false);
  const [categoryQuery, setCategoryQuery] = useState('');
  // The decimal settings are edited as text, so "0." or "12." can be typed on the way to 0.05 or 12.5.
  const [tolText, setTolText] = useState('');
  const [maxText, setMaxText] = useState('');
  const [imprestTolText, setImprestTolText] = useState('');
  const showSettings = useCallback((s: PettyCashSettings) => {
    // The imprest, reimbursement, confirmation, originals and stage settings with their defaults (an older server lacks them).
    const process = processSettings(s);
    setSettings({ ...s, ...process });
    setTolText(String(s.vatTolerance));
    setMaxText(s.maxReceiptAmount === null ? '' : String(s.maxReceiptAmount));
    setImprestTolText(String(process.imprestTolerance));
  }, []);

  useEffect(() => {
    const onChange = () => setCompanyId(Number(localStorage.getItem('selectedCompanyId') || '') || 0);
    window.addEventListener('company-changed', onChange);
    return () => window.removeEventListener('company-changed', onChange);
  }, []);

  const load = useCallback(async () => {
    if (!companyId) { setError('Select a company first.'); return; }
    try {
      const s = await pettyCashApi.setup(companyId);
      setSetup(s);
      showSettings(s.settings);
      setError(null);
    } catch (e: any) {
      setError(errorText(e, 'Petty cash setup could not be loaded'));
    }
  }, [companyId, showSettings]);

  useEffect(() => { setSetup(null); void load(); }, [load]);

  const searchVendors = useCallback(async (q: string): Promise<PickerOption[]> => {
    const rows = await pettyCashApi.searchVendors(companyId, q);
    return rows.map((v) => ({ code: v.cardCode, name: v.cardName, hint: v.vat ? `VAT ${v.vat}` : 'no VAT number' }));
  }, [companyId]);
  const set = <K extends keyof PettyCashSettings>(key: K, value: PettyCashSettings[K]) => setSettings((s) => (s ? { ...s, [key]: value } : s));

  const saveSettings = async () => {
    if (!settings) return;
    const tolerance = parseAmount(tolText);
    if (tolerance === null || tolerance < 0) {
      dispatch(showSnackbar({ message: 'The VAT difference allowed is not an amount', severity: 'error' }));
      return;
    }
    const largest = maxText.trim() ? parseAmount(maxText) : null;
    if (maxText.trim() && (largest === null || largest <= 0)) {
      dispatch(showSnackbar({ message: 'The largest receipt is not an amount above 0 (leave it empty for no limit)', severity: 'error' }));
      return;
    }
    const process = processSettings(settings);
    const problem = (() => {
      const imprestTolerance = parseAmount(imprestTolText);
      if (imprestTolerance === null || imprestTolerance < 0) return 'The imprest difference allowed is not an amount';
      if (!Number.isInteger(process.reimburseTargetDays) || process.reimburseTargetDays < 1 || process.reimburseTargetDays > 60) return 'The reimbursement target is 1 to 60 working days';
      if (process.weekendDays.length > 6) return 'Leave at least one working day in the week';
      if (process.reimbursePost && !process.reimburseBankAccount) return 'Choose the bank account the top-ups are paid from, or switch off posting reimbursements in SAP';
      if (!Number.isInteger(process.confirmWindowDays) || process.confirmWindowDays < 1 || process.confirmWindowDays > 45) return 'The confirmation window is 1 to 45 days';
      if (process.financeStages.length > MAX_FINANCE_STAGES) return `At most ${MAX_FINANCE_STAGES} finance stages after the review`;
      const incomplete = process.financeStages.findIndex((st) => !st.name.trim() || !st.role.trim());
      if (incomplete >= 0) return `Finance stage ${incomplete + 2} needs a name and a role`;
      return null;
    })();
    if (problem) {
      dispatch(showSnackbar({ message: problem, severity: 'error' }));
      return;
    }
    setSaving(true);
    try {
      // deptWarehouseName comes back from the server and is ignored on save.
      const saved = await pettyCashApi.saveSettings(companyId, {
        ...settings,
        ...process,
        vatTolerance: tolerance,
        maxReceiptAmount: largest,
        imprestTolerance: parseAmount(imprestTolText) ?? process.imprestTolerance,
        financeStages: process.financeStages.map((st) => ({ name: st.name.trim(), role: st.role.trim() })),
      });
      showSettings(saved);
      setSetup((s) => (s ? { ...s, settings: saved } : s));
      dispatch(showSnackbar({ message: 'Petty cash settings saved', severity: 'success' }));
    } catch (e: any) {
      dispatch(showSnackbar({ message: errorText(e, 'The settings could not be saved'), severity: 'error' }));
    } finally {
      setSaving(false);
    }
  };

  const saveHolder = async (input: HolderInput) => {
    await pettyCashApi.saveHolder(companyId, input);
    setHolderDialog({ open: false, holder: null });
    dispatch(showSnackbar({ message: 'Holder saved', severity: 'success' }));
    await load();
  };

  const startHandover = async (input: HandoverInput) => {
    const view = await pettyCashApi.startHandover(input);
    setHandoverDialog((d) => ({ ...d, open: false }));
    dispatch(showSnackbar({ message: `Handover started: ${view.from.name} counts the cash and confirms first`, severity: 'success' }));
    setHandoversKey((k) => k + 1);
    await load();
  };

  const saveCategory = async (input: CategoryInput) => {
    await pettyCashApi.saveCategory(companyId, input);
    setCategoryDialog({ open: false, category: null });
    dispatch(showSnackbar({ message: 'Category saved', severity: 'success' }));
    await load();
  };

  const loadStarter = async () => {
    setStarterBusy(true);
    try {
      const r = await pettyCashApi.loadStarterCategories(companyId);
      dispatch(showSnackbar({ message: `${r.added} categor${r.added === 1 ? 'y' : 'ies'} added${r.skipped ? `, ${r.skipped} already there or not usable` : ''}`, severity: 'success' }));
      await load();
    } catch (e: any) {
      dispatch(showSnackbar({ message: errorText(e, 'The starter list could not be loaded'), severity: 'error' }));
    } finally {
      setStarterBusy(false);
    }
  };

  if (error && !setup) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/petty-cash')}>Petty cash claims</Button>
        <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
      </Box>
    );
  }
  if (!setup || !settings) {
    return <Box sx={{ p: 3, display: 'flex', gap: 1, alignItems: 'center' }}><CircularProgress size={20} /><Typography>Loading…</Typography></Box>;
  }

  const currency = setup.company.currency;
  const activeDims = setup.dimensions.filter((d) => d.active);
  const dimLabel = (code: string) => setup.dimensions.find((d) => String(d.code) === code)?.label || `Dimension ${code}`;

  const holderColumns: ResponsiveTableColumn<PettyCashHolder>[] = [
    { id: 'user', header: 'Holder', cell: (h) => <Box><Typography variant="body2" fontWeight={600}>{h.userName}</Typography>{h.email ? <Typography variant="caption" color="text.secondary">{h.email}</Typography> : null}</Box> },
    {
      id: 'account',
      header: 'SAP petty cash account',
      cell: (h) => (
        <Box>
          <Typography variant="body2">{h.sapAccount} {h.sapAccountName || ''}</Typography>
          {h.auxiliaryCode ? <Typography variant="caption" color="text.secondary">Auxiliary {h.auxiliaryCode} {h.auxiliaryName || ''}</Typography> : null}
        </Box>
      ),
    },
    { id: 'float', header: `Float ${currency}`, align: 'right', cell: (h) => (h.floatAmount ? formatAmount(h.floatAmount) : '—') },
    { id: 'balance', header: `SAP balance ${currency}`, align: 'right', cell: (h) => (h.balance !== undefined && h.balance !== null ? formatAmount(h.balance) : h.auxiliaryCode ? 'Shared account' : '—') },
    { id: 'pending', header: `Not yet posted ${currency}`, align: 'right', cell: (h) => (h.pendingTotal !== undefined ? formatAmount(h.pendingTotal) : '—') },
    { id: 'outstanding', header: `Not yet reimbursed ${currency}`, align: 'right', cell: (h) => (h.outstanding !== undefined ? formatAmount(h.outstanding) : '—') },
    {
      id: 'confirmation',
      header: 'Last confirmation',
      cell: (h) => (h.lastConfirmation ? (
        <Box>
          <Typography variant="body2">{quarterLabel(h.lastConfirmation.quarter)}</Typography>
          <Typography variant="caption" color="text.secondary">
            {formatDate(h.lastConfirmation.confirmedAt)}
            {h.lastConfirmation.difference !== null && h.lastConfirmation.difference !== undefined ? ` · difference ${signedAmount(h.lastConfirmation.difference)}` : ''}
          </Typography>
        </Box>
      ) : '—'),
    },
    {
      id: 'active',
      header: 'Status',
      cell: (h) => (
        <Stack spacing={0.5} alignItems="flex-start">
          <Chip size="small" label={h.isActive ? 'Active' : 'Inactive'} color={h.isActive ? 'success' : 'default'} variant="outlined" />
          {h.pendingHandoverId ? <Chip size="small" color="warning" label="Handover pending" /> : null}
        </Stack>
      ),
    },
    {
      id: 'edit',
      header: '',
      align: 'right',
      cell: (h) => (
        <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
          {h.isActive && !h.pendingHandoverId ? <Button size="small" variant="outlined" onClick={() => setHandoverDialog({ open: true, holder: h })}>Hand over</Button> : null}
          <IconButton size="small" aria-label={`Edit holder ${h.userName}`} onClick={() => setHolderDialog({ open: true, holder: h })}><EditOutlinedIcon fontSize="small" /></IconButton>
        </Stack>
      ),
    },
  ];

  // The auxiliary (SAP Dimension 5): the holder chooses it on each receipt from the codes the account takes; a default is offered first.
  const auxLine = (account: string | null, aux: string | null | undefined, auxName: string | null | undefined) => {
    const rule = account ? setup.accountAux?.[account] : undefined;
    const type = rule ? setup.auxiliaryTypes?.find((t) => t.code === rule.type)?.name || rule.type || 'any' : null;
    if (aux) {
      return (
        <Typography variant="caption" color="text.secondary" display="block">
          Default auxiliary {aux}{auxName ? ` ${auxName}` : ''}{account && !rule ? ` (${account} takes none: clear it)` : ''}
        </Typography>
      );
    }
    if (!rule) return null;
    return (
      <Typography variant="caption" color="text.secondary" display="block">
        Auxiliary chosen on each receipt ({type} codes{rule.mandatory ? ', required' : ''})
      </Typography>
    );
  };
  // Per allocation type: the SAP item or class whose G/L rules give the account, the fixed account (when the rules give none) and the auxiliary.
  const sideCell = (
    item: string | null, itemName: string | null | undefined, itemClass: string | null,
    account: string | null, accountName: string | null | undefined, aux: string | null | undefined, auxName: string | null | undefined,
  ) => {
    const rules = item ? `SAP rules of item ${item}${itemName ? ` ${itemName}` : ''}` : itemClass ? `SAP rules of class ${itemClass}` : null;
    if (!rules && !account && !aux) return '—';
    return (
      <Box>
        {rules ? <Typography variant="body2">{rules}</Typography> : null}
        {account ? (
          <Typography variant={rules ? 'caption' : 'body2'} color={rules ? 'text.secondary' : 'text.primary'} display="block">
            {rules ? 'Else ' : ''}{account} {accountName || ''}
          </Typography>
        ) : null}
        {auxLine(account, aux, auxName)}
      </Box>
    );
  };
  const categoryColumns: ResponsiveTableColumn<PettyCashCategory>[] = [
    { id: 'name', header: 'Category', cell: (c) => <Typography variant="body2" fontWeight={600}>{c.name}</Typography> },
    {
      id: 'project',
      header: 'Project claims',
      cell: (c) => sideCell(c.projectItem, c.projectItemName, c.projectClass, c.projectAccount, c.projectAccountName, c.projectAuxiliary ?? c.dims?.['5'], c.projectAuxiliaryName),
    },
    {
      id: 'cc',
      header: 'Department claims',
      cell: (c) => sideCell(c.deptItem, c.deptItemName, c.deptClass, c.costCenterAccount, c.costCenterAccountName, c.deptAuxiliary ?? c.dims?.['5'], c.deptAuxiliaryName),
    },
    {
      id: 'dims',
      header: 'Other dimension codes',
      hideOnMobile: true,
      cell: (c) => Object.entries(c.dims || {}).filter(([k]) => k !== '5').map(([k, v]) => `${dimLabel(k)} ${v}`).join(' · ') || '—',
    },
    { id: 'vat', header: 'VAT claimable', cell: (c) => (c.vatDeductible ? 'Yes' : 'No') },
    { id: 'active', header: 'Status', cell: (c) => <Chip size="small" label={c.isActive ? 'Active' : 'Inactive'} color={c.isActive ? 'success' : 'default'} variant="outlined" /> },
    { id: 'sort', header: 'Order', align: 'right', hideOnMobile: true, cell: (c) => c.sortOrder },
    { id: 'edit', header: '', align: 'right', cell: (c) => <IconButton size="small" aria-label={`Edit category ${c.name}`} onClick={() => setCategoryDialog({ open: true, category: c })}><EditOutlinedIcon fontSize="small" /></IconButton> },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 1400 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <IconButton aria-label="Back to the claims" onClick={() => navigate('/petty-cash')}><ArrowBackIcon /></IconButton>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>Petty cash setup</Typography>
        <Typography variant="body2" color="text.secondary">{setup.company.name}</Typography>
      </Stack>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip size="small" label={setup.company.pettyCashEnabled ? 'Petty cash switched on' : 'Petty cash switched off (Company Management)'} color={setup.company.pettyCashEnabled ? 'success' : 'warning'} />
        <Chip size="small" label={setup.company.vatRegistered ? `VAT-registered${setup.company.vatCountry ? ` (${setup.company.vatCountry})` : ''}` : 'Not VAT-registered: receipts have no VAT'} variant="outlined" />
      </Stack>

      <Tabs value={tab} onChange={(_e, v) => setTab(v)} aria-label="Setup sections" sx={{ borderBottom: '1px solid #e3e8eb' }} variant="scrollable" allowScrollButtonsMobile>
        <Tab value="settings" label="Settings" />
        <Tab value="holders" label={`Holders (${setup.holders.length})`} />
        <Tab value="handovers" label="Handovers" />
        <Tab value="confirmations" label="Confirmations" />
        <Tab value="categories" label={`Expense categories (${setup.categories.length})`} />
      </Tabs>

      {tab === 'settings' ? (
        <Paper sx={{ p: 2 }}>
          <Grid container spacing={2}>
            {setup.company.vatRegistered ? (
              <>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField id="pc-set-vat" select label="VAT code for VAT receipts" value={settings.vatCode || ''} onChange={(e) => set('vatCode', e.target.value || null)} fullWidth>
                    {setup.vatCodes.map((c) => <MenuItem key={c.code} value={c.code}>{c.code} · {c.name} ({c.rate}%)</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField id="pc-set-nonvat" select label="VAT code for non-VAT receipts" value={settings.nonVatCode || ''} onChange={(e) => set('nonVatCode', e.target.value || null)} fullWidth>
                    {setup.vatCodes.map((c) => <MenuItem key={c.code} value={c.code}>{c.code} · {c.name} ({c.rate}%)</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField id="pc-set-tol" label="VAT difference allowed per receipt" value={tolText} onChange={(e) => setTolText(e.target.value)} inputProps={{ inputMode: 'decimal' }} helperText={`Typed VAT against amount x rate, in ${currency}`} fullWidth />
                </Grid>
              </>
            ) : null}
            <Grid item xs={12} md={8}>
              <SearchPicker
                id="pc-set-cash"
                label="Default cash supplier (non-VAT receipts post to it)"
                value={settings.cashSupplier ? { code: settings.cashSupplier, name: settings.cashSupplierName || '' } : null}
                onChange={(o) => setSettings((s) => (s ? { ...s, cashSupplier: o?.code || null, cashSupplierName: o?.name || null } : s))}
                search={searchVendors}
                helperText="An SAP vendor without a VAT number, e.g. Cash Purchase"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField id="pc-set-age" label="Oldest receipt accepted (days)" value={String(settings.maxReceiptAgeDays)} onChange={(e) => set('maxReceiptAgeDays', Number(e.target.value.replace(/\D/g, '')) || 0)} inputProps={{ inputMode: 'numeric' }} helperText="1 to 3650 days" fullWidth />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField id="pc-set-max" label={`Largest receipt ${currency}`} value={maxText} onChange={(e) => setMaxText(e.target.value)} inputProps={{ inputMode: 'decimal' }} helperText="Empty = no limit" fullWidth />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel control={<Switch checked={settings.blockOverLimit} onChange={(e) => set('blockOverLimit', e.target.checked)} />} label="Refuse a receipt above the limit (else only warn)" />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel control={<Switch checked={settings.requireImage} onChange={(e) => set('requireImage', e.target.checked)} />} label="Receipt photo required on every receipt" />
            </Grid>
            {setup.company.vatRegistered ? (
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel control={<Switch checked={settings.requireBuyerNamed} onChange={(e) => set('requireBuyerNamed', e.target.checked)} />} label="A VAT receipt must name the company and its VAT number" />
              </Grid>
            ) : null}
            <Grid item xs={12} sm={6} md={4}>
              <TextField id="pc-set-reqtype" select label="Approval follows the PR flows of type" value={settings.requisitionType} onChange={(e) => set('requisitionType', e.target.value)} helperText="With the claim's company, project or department, facility type and cycle" fullWidth>
                {REQUISITION_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField id="pc-set-role" label="Finance review role" value={settings.reviewRole} onChange={(e) => set('reviewRole', e.target.value.trim())} helperText="Any user holding this role (main or additional) in the company, e.g. APTeam" fullWidth />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1 }}>G/L accounts</Typography>
              <Typography variant="body2" color="text.secondary">
                Each receipt gets the account SAP's Advanced G/L rules give the same purchase. Each expense category names the SAP item or item
                class to look up, separately for project and department claims.
              </Typography>
              <Box component="ul" sx={{ mt: 0.5, mb: 1.5, pl: 2.5, color: 'text.secondary' }}>
                <Typography component="li" variant="body2">
                  Project claims follow the rules of the project&apos;s warehouse, department claims those of the warehouse below.
                </Typography>
                <Typography component="li" variant="body2">
                  Warehouses of type Charge Back, Special Project or Customer Stock keep the cost on their clearing account until it is billed, as a
                  purchase through PR, PO and goods receipt does. Other warehouses, and companies without warehouse types, book it as a cost at once.
                </Typography>
                <Typography component="li" variant="body2">
                  When no rule fits, the category&apos;s fixed account applies. Finance can still change the account of any receipt.
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <TextField
                    id="pc-set-deptwhs"
                    select
                    label="Department claims follow the G/L rules of warehouse"
                    value={settings.deptWarehouse || ''}
                    onChange={(e) => setSettings((s) => (s ? { ...s, deptWarehouse: e.target.value || null, deptWarehouseName: null } : s))}
                    helperText={settings.deptWarehouse ? 'Department claims get the account this warehouse\'s rules give (by its code and type)' : 'Usually the head office warehouse. Not set: department claims use the categories\' fixed accounts'}
                    fullWidth
                    SelectProps={{ displayEmpty: true }}
                    InputLabelProps={{ shrink: true }}
                  >
                    <MenuItem value=""><em>Not set</em></MenuItem>
                    {settings.deptWarehouse && !(setup.warehouses || []).some((w) => w.code === settings.deptWarehouse) ? (
                      <MenuItem value={settings.deptWarehouse}>{settings.deptWarehouse} {settings.deptWarehouseName || ''}</MenuItem>
                    ) : null}
                    {(setup.warehouses || []).map((w) => (
                      <MenuItem key={w.code} value={w.code}>{w.code} {w.name}{w.type ? ` · ${w.type}` : ''}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <ProcessSettings
              companyId={companyId}
              currency={currency}
              value={processSettings(settings)}
              reviewRole={settings.reviewRole}
              onChange={(patch) => setSettings((s) => (s ? { ...s, ...patch } : s))}
              toleranceText={imprestTolText}
              onToleranceText={setImprestTolText}
            />
          </Stack>
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button onClick={() => { void saveSettings(); }} disabled={saving}>{saving ? 'Saving…' : 'Save settings'}</Button>
          </Stack>
        </Paper>
      ) : null}

      {tab === 'holders' ? (
        <Paper sx={{ p: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto' }}>Each holder pays receipts from an SAP cash account: their own, or a shared one where their auxiliary code (Dimension 5) tells their cash apart.</Typography>
            <Button startIcon={<AddIcon />} onClick={() => setHolderDialog({ open: true, holder: null })}>Add holder</Button>
          </Stack>
          <ResponsiveTable ariaLabel="Petty cash holders" columns={holderColumns} rows={setup.holders} getRowKey={(h) => h.holderId} emptyMessage="No holders yet" />
        </Paper>
      ) : null}

      {tab === 'handovers' ? (
        <Paper sx={{ p: 2 }}>
          <HandoversTab companyId={companyId} currency={currency} reloadKey={handoversKey} onChanged={() => { void load(); }} />
        </Paper>
      ) : null}

      {tab === 'confirmations' ? (
        <Paper sx={{ p: 2 }}>
          {processSettings(setup.settings).quarterlyConfirm ? null : (
            <Alert severity="info" sx={{ mb: 1.5 }}>Quarterly confirmations are switched off in the settings: holders are not asked for one.</Alert>
          )}
          <ConfirmationsTab companyId={companyId} currency={currency} windowDays={processSettings(setup.settings).confirmWindowDays} />
        </Paper>
      ) : null}

      {tab === 'categories' ? (
        <Paper sx={{ p: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto' }}>
              What holders choose on each receipt: for project and for department claims, the G/L account (from SAP&apos;s Advanced G/L rules, else the fixed
              account) and the auxiliary its receipts carry.
            </Typography>
            <Button variant="outlined" onClick={() => { void loadStarter(); }} disabled={starterBusy}>{starterBusy ? 'Loading…' : 'Load starter list'}</Button>
            <Button startIcon={<AddIcon />} onClick={() => setCategoryDialog({ open: true, category: null })}>Add category</Button>
          </Stack>
          <TextField
            id="pc-cat-search"
            size="small"
            label="Search categories"
            placeholder="Name, account or auxiliary"
            value={categoryQuery}
            onChange={(e) => setCategoryQuery(e.target.value.slice(0, 100))}
            sx={{ mb: 1.5, width: { xs: '100%', sm: 360 } }}
            InputLabelProps={{ shrink: true }}
          />
          <ResponsiveTable
            ariaLabel="Expense categories"
            columns={categoryColumns}
            rows={[...setup.categories].filter((c) => categoryListMatches(c, categoryQuery)).sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))}
            getRowKey={(c) => c.categoryId}
            emptyMessage={categoryQuery.trim() ? 'No category matches the search' : 'No categories yet: load the starter list or add one'}
          />
        </Paper>
      ) : null}

      <HolderDialog
        open={holderDialog.open}
        companyId={companyId}
        currency={currency}
        holder={holderDialog.holder}
        holders={setup.holders}
        users={setup.users}
        onClose={() => setHolderDialog({ open: false, holder: null })}
        onSave={saveHolder}
      />
      <HandoverDialog
        open={handoverDialog.open}
        companyId={companyId}
        currency={currency}
        holder={handoverDialog.holder}
        holders={setup.holders}
        users={setup.users}
        onClose={() => setHandoverDialog((d) => ({ ...d, open: false }))}
        onSave={startHandover}
      />
      <CategoryDialog
        open={categoryDialog.open}
        companyId={companyId}
        category={categoryDialog.category}
        dimensions={activeDims}
        itemClasses={setup.itemClasses || []}
        nextSortOrder={(setup.categories.reduce((m, c) => Math.max(m, c.sortOrder), 0) || 0) + 10}
        onClose={() => setCategoryDialog({ open: false, category: null })}
        onSave={saveCategory}
      />
    </Box>
  );
};

export default PettyCashSetup;
